import {
  read,
  update,
  partnerW9,
} from '@/api/partner';
import {
  read as getPages,
} from '@/api/page_partner';
import * as mutations from './mutations';
import * as actions from './actions';

const initialState = () => ({
  id: null,
  email: null,
  firstName: null,
  lastName: null,
  companyName: null,
  companyAddress: null,
  websiteUrl: null,
  phone: null,
  plan: null,
  leadOwner: null,
  cpActivation: null,
  user: null,
  status: null,
  hasPage: null,
  payoutMethod: null,
  pages: [],
  selectedPage: {},
  w9: { status: null, date: null },
});
const FIELD_MAP = {
  id: 'id',
  email: 'email',
  firstName: 'firstName',
  lastName: 'lastName',
  companyName: 'companyName',
  companyAddress: 'companyAddress',
  siteUrl: 'websiteUrl',
  phone: 'phone',
  plan: 'plan',
  leadOwner: 'leadOwner',
  cpActivation: 'cpActivation',
  user: 'user',
  status: 'status',
  hasPage: 'hasPage',
  payoutMethod: 'payoutMethod',
};
function mapFields(payload, mapping) {
  const result = {};
  Object.keys(mapping).forEach((key) => {
    if (key in payload) {
      result[mapping[key]] = payload[key];
    }
  });
  return result;
}
function mapFieldsToServer(payload, mapping) {
  const result = {};
  Object.keys(mapping).forEach((key) => {
    if (Object.hasOwn(payload, mapping[key])) {
      result[key] = payload[mapping[key]];
    }
  });
  return result;
}

export default {
  namespaced: true,
  state: () => initialState(),
  mutations: {
    /**
     * Set partner from payload
     * @param state
     * @param payload
     */
    [mutations.PARTNER_LOAD](state, payload) {
      Object.keys(payload).forEach((key) => {
        state[key] = payload[key];
      });
    },

    /**
     * Update partner fields from payload
     * @param state
     * @param payload
     */
    [mutations.UPDATE](state, payload) {
      Object.keys(payload).forEach((key) => {
        state[key] = payload[key];
      });
    },

    /**
     * Clear contractor state
     * @param state
     */
    [mutations.CLEAR](state) {
      const init = initialState();
      Object.keys(state).forEach((key) => {
        state[key] = init[key];
      });
    },

    [mutations.SET_PAGES](state, pages) {
      state.pages = pages;
      if (pages.length > 0) {
        const foundPage = pages.find((item) => item.id === state.selectedPage?.id);
        state.selectedPage = foundPage || { ...pages[0] };
      } else {
        state.selectedPage = {};
      }
    },

    [mutations.SET_W9](state, payload) {
      state.w9 = {
        status: payload && payload.status != null ? payload.status : null,
        date: payload && payload.date != null ? payload.date : null,
      };
    },

    [mutations.SET_SELECTED_PAGE](state, selectedPage = null) {
      if (selectedPage) {
        state.selectedPage = selectedPage;
      } else if (state.pages && state.pages.length > 0) {
        state.selectedPage = { ...state.pages[0] };
      } else {
        state.selectedPage = {};
      }
    },
  },

  getters: {
    w9: (state) => state.w9,
    w9Status: (state) => state.w9.status,
  },

  actions: {
    /**
     * Load partner
     * @param context
     * @param partnerId
     * @returns {Promise<boolean>}
     */
    async [actions.PARTNER](context, partnerId) {
      if (!partnerId) {
        context.commit(mutations.CLEAR);
        return true;
      }
      const response = await read(partnerId);
      if (response.data) {
        const normalizedData = mapFields(response.data, FIELD_MAP);
        context.commit(mutations.PARTNER_LOAD, normalizedData);
      } else {
        context.commit(mutations.CLEAR);
      }
      return true;
    },

    /**
     * Update partner on server
     * @param context
     * @param payload
     * @returns {Promise<void>}
     */
    async [actions.UPDATE_PARTNER](context, payload) {
      const data = mapFieldsToServer(payload, FIELD_MAP);
      const response = await update(data);
      if (response.data) {
        const normalizedData = mapFields(response.data, FIELD_MAP);
        context.commit(mutations.UPDATE, normalizedData);
      }
    },

    /**
     * Clear partner state
     * @param context
     * @returns {Promise<void>}
     */
    async [actions.CLEAR_PARTNER](context) {
      context.commit(mutations.CLEAR);
    },

    /**
     * Load partner's W-9 status into the store
     * @param context
     * @returns {Promise<void>}
     */
    async [actions.LOAD_W9](context) {
      if (!context.state.id) {
        context.commit(mutations.SET_W9, {});
        return;
      }
      const response = await partnerW9(context.state.id);
      context.commit(mutations.SET_W9, response.data || {});
    },

    async [actions.LOAD_PAGES](context) {
      let pages = [];

      if (context.state.id) {
        const response = await getPages(context.state.id);
        if (response.data) {
          pages = [response.data]; // page partner second iteration
        }
      }

      context.commit(mutations.SET_PAGES, pages);
    },

    [actions.SET_SELECTED_PAGE](context, payload) {
      context.commit(mutations.SET_SELECTED_PAGE, payload);
    },
  },
};
